import { logger, sanitizeError } from "./logger.js";
import {
  validateRFC,
  validateEmail,
  validatePhoneNumber,
  findMatchingState,
  findMatchingTown,
  findMatchingCompanySize,
  findMatchingCountryCode,
  findMatchingCustomerCompanyUser,
  findMatchingCategory,
  findMatchingSubcategory,
  findMatchingRequirementStatus,
  getRequirementsExcelFieldDefinitions,
} from "./common.js";
import {
  findExactBuyerCompany,
  findPartialBuyerCompany,
} from "./excelCompanyMatch.js";

export const EXCEL_HEADER_RAZON_SOCIAL = "Razón Social";
export const EXCEL_HEADER_NOMBRE_COMERCIAL = "Nombre Comercial";
export const EXCEL_HEADER_RFC = "RFC";
export const EXCEL_HEADER_TAMANO = "Tamaño";
export const EXCEL_HEADER_DIRECCION = "Dirección";
export const EXCEL_HEADER_ESTADO = "Estado";
export const EXCEL_HEADER_MUNICIPIO = "Municipio";
export const EXCEL_HEADER_CONTACTO = "Contacto Principal";
export const EXCEL_HEADER_CORREO_CONTACTO =
  "Correo Electrónico Contacto Principal";
export const EXCEL_HEADER_CARGO_CONTACTO = "Cargo Contacto Principal";
export const EXCEL_HEADER_NOMBRE_USUARIO = "Nombre Completo Usuario Comprador";
export const EXCEL_HEADER_CORREO_USUARIO =
  "Correo Electrónico Usuario Comprador";
export const EXCEL_HEADER_CARGO_USUARIO = "Cargo Usuario Comprador";
export const EXCEL_HEADER_CODIGO_PAIS = "Código de País (MX, US, FR, CA)";
export const EXCEL_HEADER_TELEFONO = "Número de Teléfono";
export const EXCEL_HEADER_NOMBRE_REQUERIMIENTO = "Nombre de Requerimiento";
export const EXCEL_HEADER_USUARIO_COMPRADOR = "Usuario Comprador";
export const EXCEL_HEADER_CATEGORIA = "Categoría";
export const EXCEL_HEADER_SUBCATEGORIA = "Subcategoría";
export const EXCEL_HEADER_DESCRIPCION_REQUERIMIENTO =
  "Descripción Requerimiento";

export const DUPLICATE_USER_FIELD_NAME = "nombre";
export const DUPLICATE_USER_FIELD_EMAIL = "email";
export const DUPLICATE_USER_FIELD_UNKNOWN = "unknown";

const findDuplicateExcelHeaders = (headers) => {
  const headerCounts = {};
  headers.forEach((header) => {
    headerCounts[header] = (headerCounts[header] || 0) + 1;
  });
  return Object.entries(headerCounts)
    .filter(([, count]) => count > 1)
    .map(([header]) => header);
};

const duplicateExcelHeaderError = (headers, t) => {
  const duplicateHeaders = findDuplicateExcelHeaders(headers);
  if (duplicateHeaders.length === 0) {
    return null;
  }
  return t("pages.buyerCompanyForm.excelDuplicateHeadersError", {
    headers: duplicateHeaders.join(", "),
  });
};

/**
 * Classifies which buyer-user field collided with an existing user.
 * Unexpected shapes are logged and marked unknown so the row is still rejected.
 *
 * @param {object} duplicateUser
 * @param {string} buyerName
 * @param {string} buyerEmail
 * @param {string} normalizedBuyerEmail
 * @returns {{ field: string, value: string|null }}
 */
export const classifyDuplicateBuyerUser = (
  duplicateUser,
  buyerName,
  buyerEmail,
  normalizedBuyerEmail,
) => {
  if (duplicateUser.nombre === buyerName) {
    return { field: DUPLICATE_USER_FIELD_NAME, value: buyerName };
  }
  if (
    duplicateUser.correoElectronico?.trim().toLowerCase() ===
    normalizedBuyerEmail
  ) {
    return { field: DUPLICATE_USER_FIELD_EMAIL, value: buyerEmail };
  }
  logger.warn("Unexpected duplicate user structure", { duplicateUser });
  return { field: DUPLICATE_USER_FIELD_UNKNOWN, value: null };
};

const STATUS_OPTIONAL = "Opcional";
const STATUS_NOT_REQUIRED = "No Requerido";
const STATUS_REQUIRED = "Requerido";
const STATUS_VALUE_OPTIONAL = 0;
const STATUS_VALUE_NOT_REQUIRED = 1;
const STATUS_VALUE_REQUIRED = 2;

const BUYER_COMPANY_REQUIRED_HEADERS = [
  EXCEL_HEADER_RAZON_SOCIAL,
  EXCEL_HEADER_NOMBRE_COMERCIAL,
  EXCEL_HEADER_RFC,
  EXCEL_HEADER_TAMANO,
  EXCEL_HEADER_DIRECCION,
  EXCEL_HEADER_ESTADO,
  EXCEL_HEADER_MUNICIPIO,
  EXCEL_HEADER_CONTACTO,
  EXCEL_HEADER_CORREO_CONTACTO,
  EXCEL_HEADER_CARGO_CONTACTO,
];

const BUYER_USER_REQUIRED_HEADERS = [
  EXCEL_HEADER_NOMBRE_USUARIO,
  EXCEL_HEADER_CORREO_USUARIO,
  EXCEL_HEADER_CARGO_USUARIO,
  EXCEL_HEADER_CODIGO_PAIS,
  EXCEL_HEADER_TELEFONO,
];

const REQUIREMENT_FIXED_HEADERS = [
  EXCEL_HEADER_NOMBRE_REQUERIMIENTO,
  EXCEL_HEADER_USUARIO_COMPRADOR,
  EXCEL_HEADER_CATEGORIA,
  EXCEL_HEADER_SUBCATEGORIA,
  EXCEL_HEADER_DESCRIPCION_REQUERIMIENTO,
];

const readExcelCell = (row, headers, header) =>
  row[headers.indexOf(header)]?.toString().trim();

const isEmptyExcelValue = (field) => !field || field === "";

const isBlankExcelRow = (fields) => fields.every(isEmptyExcelValue);

const missingExcelLabels = (pairs) => {
  const missing = [];
  for (const [value, label] of pairs) {
    if (!value) {
      missing.push(label);
    }
  }
  return missing;
};

const excelTooShortResult = (data, t) => {
  if (data.length >= 2) {
    return null;
  }
  return {
    isValid: false,
    error: t("pages.buyerCompanyForm.excelEmptyError"),
  };
};

const duplicateHeaderResult = (headers, t) => {
  const duplicateHeaderError = duplicateExcelHeaderError(headers, t);
  if (!duplicateHeaderError) {
    return null;
  }
  return { isValid: false, error: duplicateHeaderError };
};

const missingHeadersResult = (headers, requiredHeaders, t, messageKey) => {
  const missingHeaders = requiredHeaders.filter(
    (header) => !headers.includes(header),
  );
  if (missingHeaders.length === 0) {
    return null;
  }
  return {
    isValid: false,
    error: t(messageKey, { headers: missingHeaders.join(", ") }),
  };
};

const excelRowErrorResult = (invalidRows, t, extra) => {
  let errorMessage = null;
  if (invalidRows.length > 0) {
    errorMessage = t("pages.buyerCompanyForm.excelInvalidRowsError", {
      count: invalidRows.length,
    });
  }
  return {
    isValid: invalidRows.length === 0,
    error: errorMessage,
    invalidRows,
    ...extra,
  };
};

const buyerIdentity = (company) => ({
  razonSocial: company.razonSocial || "",
  nombreComercial: company.nombreComercial || "",
  rfc: company.rfc || "",
});

const buyerIssue = (rowNumber, type, company, extra) => ({
  rowNumber,
  type,
  ...buyerIdentity(company),
  ...extra,
});

const readBuyerCompanyCells = (row, headers) => {
  const razonSocial = readExcelCell(row, headers, EXCEL_HEADER_RAZON_SOCIAL);
  const nombreComercial = readExcelCell(
    row,
    headers,
    EXCEL_HEADER_NOMBRE_COMERCIAL,
  );
  const rfc = readExcelCell(row, headers, EXCEL_HEADER_RFC);
  const tamano = readExcelCell(row, headers, EXCEL_HEADER_TAMANO);
  const direccion = readExcelCell(row, headers, EXCEL_HEADER_DIRECCION);
  const estado = readExcelCell(row, headers, EXCEL_HEADER_ESTADO);
  const municipio = readExcelCell(row, headers, EXCEL_HEADER_MUNICIPIO);
  const contactoPrincipal = readExcelCell(row, headers, EXCEL_HEADER_CONTACTO);
  const correoContacto = readExcelCell(
    row,
    headers,
    EXCEL_HEADER_CORREO_CONTACTO,
  );
  const cargoContacto = readExcelCell(
    row,
    headers,
    EXCEL_HEADER_CARGO_CONTACTO,
  );
  return {
    razonSocial,
    nombreComercial,
    rfc,
    tamano,
    direccion,
    estado,
    municipio,
    contactoPrincipal,
    correoContacto,
    cargoContacto,
    companyFields: [
      razonSocial,
      nombreComercial,
      rfc,
      tamano,
      direccion,
      estado,
      municipio,
      contactoPrincipal,
      correoContacto,
      cargoContacto,
    ],
  };
};

const buyerCompanyFieldIssue = (company, rowNumber) => {
  const missingCompanyFields = missingExcelLabels([
    [company.razonSocial, EXCEL_HEADER_RAZON_SOCIAL],
    [company.nombreComercial, EXCEL_HEADER_NOMBRE_COMERCIAL],
    [company.rfc, EXCEL_HEADER_RFC],
    [company.tamano, EXCEL_HEADER_TAMANO],
    [company.direccion, EXCEL_HEADER_DIRECCION],
    [company.estado, EXCEL_HEADER_ESTADO],
    [company.municipio, EXCEL_HEADER_MUNICIPIO],
    [company.contactoPrincipal, EXCEL_HEADER_CONTACTO],
    [company.correoContacto, EXCEL_HEADER_CORREO_CONTACTO],
    [company.cargoContacto, EXCEL_HEADER_CARGO_CONTACTO],
  ]);
  if (missingCompanyFields.length === 0) {
    return null;
  }
  return buyerIssue(rowNumber, "company", company, {
    missingFields: missingCompanyFields,
  });
};

const buyerRfcIssue = (company, rowNumber) => {
  const rfcValidation = validateRFC(company.rfc);
  if (rfcValidation.isValid) {
    return null;
  }
  return buyerIssue(rowNumber, "rfc", company, { error: rfcValidation.error });
};

const buyerContactEmailIssue = (company, rowNumber) => {
  const emailValidation = validateEmail(company.correoContacto);
  if (emailValidation.isValid) {
    return null;
  }
  return buyerIssue(rowNumber, "email", company, {
    field: EXCEL_HEADER_CORREO_CONTACTO,
    value: company.correoContacto,
    error: emailValidation.error,
  });
};

const buyerPartialMatchIssue = (company, rowNumber, existingCompanies, t) => {
  const partialMatchCompany = findPartialBuyerCompany(existingCompanies, {
    razonSocial: company.razonSocial,
    nombreComercial: company.nombreComercial,
    rfc: company.rfc,
  });
  if (!partialMatchCompany) {
    return null;
  }
  return buyerIssue(rowNumber, "companyFieldsMismatch", company, {
    error: t("pages.buyerCompanyForm.excelCompanyFieldsMismatchError"),
  });
};

const buyerStateIssue = (company, rowNumber, states, t) => {
  const matchedState = findMatchingState(company.estado, states);
  if (!matchedState) {
    return {
      issue: buyerIssue(rowNumber, "state", company, {
        field: EXCEL_HEADER_ESTADO,
        value: company.estado,
        error: t("pages.buyerCompanyForm.excelInvalidStateError", {
          state: company.estado,
        }),
      }),
    };
  }
  return { matchedState };
};

const buyerTownIssue = (company, rowNumber, towns, matchedState, t) => {
  const matchedTown = findMatchingTown(company.municipio, towns, matchedState._id);
  if (!matchedTown) {
    return {
      issue: buyerIssue(rowNumber, "town", company, {
        field: EXCEL_HEADER_MUNICIPIO,
        value: company.municipio,
        error: t("pages.buyerCompanyForm.excelInvalidTownError", {
          town: company.municipio,
          state: matchedState.nombre,
        }),
      }),
    };
  }
  return { matchedTown };
};

const buyerSizeIssue = (company, rowNumber, companySizeOptions, t) => {
  const matchedSize = findMatchingCompanySize(company.tamano, companySizeOptions);
  if (!matchedSize) {
    return {
      issue: buyerIssue(rowNumber, "size", company, {
        field: EXCEL_HEADER_TAMANO,
        value: company.tamano,
        error: t("pages.buyerCompanyForm.excelInvalidSizeError", {
          size: company.tamano,
        }),
      }),
    };
  }
  return { matchedSize };
};

const readBuyerUserCells = (row, headers) => ({
  nombreComprador: readExcelCell(row, headers, EXCEL_HEADER_NOMBRE_USUARIO),
  correoComprador: readExcelCell(row, headers, EXCEL_HEADER_CORREO_USUARIO),
  cargoComprador: readExcelCell(row, headers, EXCEL_HEADER_CARGO_USUARIO),
  codigoPais: readExcelCell(row, headers, EXCEL_HEADER_CODIGO_PAIS),
  numeroTelefono: readExcelCell(row, headers, EXCEL_HEADER_TELEFONO),
});

const buyerUserPresence = (user) => {
  const values = [
    user.nombreComprador,
    user.correoComprador,
    user.cargoComprador,
    user.codigoPais,
    user.numeroTelefono,
  ];
  return {
    hasUserData: values.some((value) => Boolean(value)),
    hasAllUserData: values.every((value) => Boolean(value)),
  };
};

const buyerUserIncompleteIssue = (user, company, rowNumber) => {
  const { hasUserData, hasAllUserData } = buyerUserPresence(user);
  if (!hasUserData || hasAllUserData) {
    return null;
  }
  return buyerIssue(rowNumber, "user", company, {
    missingFields: missingExcelLabels([
      [user.nombreComprador, EXCEL_HEADER_NOMBRE_USUARIO],
      [user.correoComprador, EXCEL_HEADER_CORREO_USUARIO],
      [user.cargoComprador, EXCEL_HEADER_CARGO_USUARIO],
      [user.codigoPais, EXCEL_HEADER_CODIGO_PAIS],
      [user.numeroTelefono, EXCEL_HEADER_TELEFONO],
    ]),
  });
};

const buyerUserEmailIssue = (user, company, rowNumber) => {
  const userEmailValidation = validateEmail(user.correoComprador);
  if (userEmailValidation.isValid) {
    return null;
  }
  return buyerIssue(rowNumber, "email", company, {
    field: EXCEL_HEADER_CORREO_USUARIO,
    value: user.correoComprador,
    error: userEmailValidation.error,
  });
};

const buyerUserCountryIssue = (user, company, rowNumber, countryCodeOptions, t) => {
  const matchedCountryCode = findMatchingCountryCode(
    user.codigoPais,
    countryCodeOptions,
  );
  if (matchedCountryCode) {
    return null;
  }
  return buyerIssue(rowNumber, "countryCode", company, {
    field: EXCEL_HEADER_CODIGO_PAIS,
    value: user.codigoPais,
    error: t("pages.buyerCompanyForm.excelInvalidCountryCodeError", {
      countryCode: user.codigoPais,
    }),
  });
};

const buyerUserPhoneIssue = (user, company, rowNumber, t) => {
  const phoneValidation = validatePhoneNumber(user.numeroTelefono);
  if (phoneValidation.isValid) {
    return null;
  }
  return buyerIssue(rowNumber, "phone", company, {
    field: EXCEL_HEADER_TELEFONO,
    value: user.numeroTelefono,
    error: t("pages.buyerCompanyForm.excelInvalidPhoneError", {
      phoneNumber: user.numeroTelefono,
    }),
  });
};

const buyerUserContactIssue = (
  user,
  company,
  rowNumber,
  countryCodeOptions,
  t,
) =>
  buyerUserEmailIssue(user, company, rowNumber) ||
  buyerUserCountryIssue(user, company, rowNumber, countryCodeOptions, t) ||
  buyerUserPhoneIssue(user, company, rowNumber, t);

const duplicateUserInFileIssue = (user, company, rowNumber, companyGroup, t) => {
  const normalizedBuyerEmail = user.correoComprador.toLowerCase();
  const duplicateInFile = companyGroup.users.some(
    (existingUser) =>
      existingUser.correoElectronico.trim().toLowerCase() ===
      normalizedBuyerEmail,
  );
  if (!duplicateInFile) {
    return null;
  }
  return buyerIssue(rowNumber, "duplicateUser", company, {
    field: "email",
    value: user.correoComprador,
    error: t("pages.buyerCompanyForm.excelDuplicateUserError", {
      field: "email",
      value: user.correoComprador,
    }),
  });
};

const loadExistingBuyerUsers = async (
  exactMatchCompany,
  existingUsersByCompanyId,
  getCustomerCompanyUsersByCompanyId,
  navigate,
) => {
  let existingUsers = existingUsersByCompanyId.get(exactMatchCompany._id);
  if (existingUsers) {
    return existingUsers;
  }
  existingUsers = await getCustomerCompanyUsersByCompanyId(
    exactMatchCompany._id,
    navigate,
  );
  existingUsersByCompanyId.set(
    exactMatchCompany._id,
    Array.isArray(existingUsers) ? existingUsers : [],
  );
  return existingUsersByCompanyId.get(exactMatchCompany._id);
};

const duplicateExistingUserIssue = async (
  user,
  company,
  rowNumber,
  exactMatchCompany,
  ctx,
) => {
  const { getCustomerCompanyUsersByCompanyId, navigate, t } = ctx;
  if (!(exactMatchCompany && getCustomerCompanyUsersByCompanyId && navigate)) {
    return null;
  }
  try {
    const existingUsers = await loadExistingBuyerUsers(
      exactMatchCompany,
      ctx.existingUsersByCompanyId,
      getCustomerCompanyUsersByCompanyId,
      navigate,
    );
    const normalizedBuyerEmail = user.correoComprador.toLowerCase();
    const duplicateUser = existingUsers.find(
      (existingUser) =>
        existingUser.nombre === user.nombreComprador ||
        existingUser.correoElectronico?.trim().toLowerCase() ===
          normalizedBuyerEmail,
    );
    if (!duplicateUser) {
      return null;
    }
    const { field: duplicateField, value: duplicateValue } =
      classifyDuplicateBuyerUser(
        duplicateUser,
        user.nombreComprador,
        user.correoComprador,
        normalizedBuyerEmail,
      );
    return buyerIssue(rowNumber, "duplicateUser", company, {
      field: duplicateField,
      value: duplicateValue,
      error: t("pages.buyerCompanyForm.excelDuplicateUserError", {
        field: duplicateField,
        value: duplicateValue,
      }),
    });
  } catch (error) {
    logger.error("Error checking for duplicate users:", {
      err: sanitizeError(error),
    });
    return null;
  }
};

const rememberBuyerCompany = (
  company,
  rowNumber,
  companyGroups,
  matchedState,
  matchedTown,
  matchedSize,
  exactMatchCompany,
) => {
  const companyId = `${company.razonSocial}-${company.rfc}`;
  if (!companyGroups.has(companyId)) {
    companyGroups.set(companyId, {
      companyData: {
        razonSocial: company.razonSocial,
        nombreComercial: company.nombreComercial,
        RFC: company.rfc,
        tamanoDeLaEmpresa: matchedSize.value,
        direccion: company.direccion,
        estado: matchedState._id,
        municipio: matchedTown._id,
        contactoPrincipal: {
          nombre: company.contactoPrincipal,
          correoElectronico: company.correoContacto,
          cargo: company.cargoContacto,
        },
      },
      users: [],
      rowNumbers: [],
      existingCompanyId: exactMatchCompany?._id,
    });
  }
  companyGroups.get(companyId).rowNumbers.push(rowNumber);
  return companyId;
};

const appendBuyerUser = async (
  user,
  company,
  rowNumber,
  companyId,
  exactMatchCompany,
  ctx,
) => {
  const companyGroup = ctx.companyGroups.get(companyId);
  const inFileIssue = duplicateUserInFileIssue(
    user,
    company,
    rowNumber,
    companyGroup,
    ctx.t,
  );
  if (inFileIssue) {
    ctx.invalidRows.push(inFileIssue);
    return;
  }
  const existingIssue = await duplicateExistingUserIssue(
    user,
    company,
    rowNumber,
    exactMatchCompany,
    ctx,
  );
  if (existingIssue) {
    ctx.invalidRows.push(existingIssue);
    return;
  }
  const matchedCountryCode = findMatchingCountryCode(
    user.codigoPais,
    ctx.countryCodeOptions,
  );
  companyGroup.users.push({
    nombre: user.nombreComprador,
    correoElectronico: user.correoComprador,
    cargo: user.cargoComprador,
    codigoPais: matchedCountryCode.value,
    numeroTelefono: user.numeroTelefono,
  });
};

const buyerReferenceIssue = (company, rowNumber, ctx) => {
  const stateResult = buyerStateIssue(
    company,
    rowNumber,
    ctx.validationFunctions.states,
    ctx.t,
  );
  if (stateResult.issue) {
    return stateResult;
  }
  const townResult = buyerTownIssue(
    company,
    rowNumber,
    ctx.validationFunctions.towns,
    stateResult.matchedState,
    ctx.t,
  );
  if (townResult.issue) {
    return townResult;
  }
  const sizeResult = buyerSizeIssue(
    company,
    rowNumber,
    ctx.companySizeOptions,
    ctx.t,
  );
  if (sizeResult.issue) {
    return sizeResult;
  }
  return {
    matchedState: stateResult.matchedState,
    matchedTown: townResult.matchedTown,
    matchedSize: sizeResult.matchedSize,
  };
};

const processBuyerCompanyRow = async (ctx) => {
  const company = readBuyerCompanyCells(ctx.row, ctx.headers);
  if (isBlankExcelRow(company.companyFields)) {
    return;
  }
  const fieldIssue = buyerCompanyFieldIssue(company, ctx.rowNumber);
  if (fieldIssue) {
    ctx.invalidRows.push(fieldIssue);
    return;
  }
  const rfcIssue = buyerRfcIssue(company, ctx.rowNumber);
  if (rfcIssue) {
    ctx.invalidRows.push(rfcIssue);
    return;
  }
  const emailIssue = buyerContactEmailIssue(company, ctx.rowNumber);
  if (emailIssue) {
    ctx.invalidRows.push(emailIssue);
    return;
  }
  const companyIdentity = {
    razonSocial: company.razonSocial,
    nombreComercial: company.nombreComercial,
    rfc: company.rfc,
  };
  const exactMatchCompany = findExactBuyerCompany(
    ctx.existingCompanies,
    companyIdentity,
  );
  const partialIssue = buyerPartialMatchIssue(
    company,
    ctx.rowNumber,
    ctx.existingCompanies,
    ctx.t,
  );
  if (partialIssue) {
    ctx.invalidRows.push(partialIssue);
    return;
  }
  const references = buyerReferenceIssue(company, ctx.rowNumber, ctx);
  if (references.issue) {
    ctx.invalidRows.push(references.issue);
    return;
  }
  const companyId = rememberBuyerCompany(
    company,
    ctx.rowNumber,
    ctx.companyGroups,
    references.matchedState,
    references.matchedTown,
    references.matchedSize,
    exactMatchCompany,
  );
  const user = readBuyerUserCells(ctx.row, ctx.headers);
  const incompleteIssue = buyerUserIncompleteIssue(user, company, ctx.rowNumber);
  if (incompleteIssue) {
    ctx.invalidRows.push(incompleteIssue);
    return;
  }
  if (!buyerUserPresence(user).hasAllUserData) {
    return;
  }
  const contactIssue = buyerUserContactIssue(
    user,
    company,
    ctx.rowNumber,
    ctx.countryCodeOptions,
    ctx.t,
  );
  if (contactIssue) {
    ctx.invalidRows.push(contactIssue);
    return;
  }
  await appendBuyerUser(
    user,
    company,
    ctx.rowNumber,
    companyId,
    exactMatchCompany,
    ctx,
  );
};

const readRequirementCells = (row, headers) => {
  const nombreRequerimiento = readExcelCell(
    row,
    headers,
    EXCEL_HEADER_NOMBRE_REQUERIMIENTO,
  );
  const usuarioComprador = readExcelCell(
    row,
    headers,
    EXCEL_HEADER_USUARIO_COMPRADOR,
  );
  const categoria = readExcelCell(row, headers, EXCEL_HEADER_CATEGORIA);
  const subcategoria = readExcelCell(row, headers, EXCEL_HEADER_SUBCATEGORIA);
  const descripcion = readExcelCell(
    row,
    headers,
    EXCEL_HEADER_DESCRIPCION_REQUERIMIENTO,
  );
  return {
    nombreRequerimiento,
    usuarioComprador,
    categoria,
    subcategoria,
    descripcion,
    mainFields: [
      nombreRequerimiento,
      usuarioComprador,
      categoria,
      subcategoria,
      descripcion,
    ],
  };
};

const requirementMissingFieldsIssue = (rowData, rowNumber) => {
  const missingFields = missingExcelLabels([
    [rowData.nombreRequerimiento, EXCEL_HEADER_NOMBRE_REQUERIMIENTO],
    [rowData.usuarioComprador, EXCEL_HEADER_USUARIO_COMPRADOR],
    [rowData.categoria, EXCEL_HEADER_CATEGORIA],
    [rowData.subcategoria, EXCEL_HEADER_SUBCATEGORIA],
    [rowData.descripcion, EXCEL_HEADER_DESCRIPCION_REQUERIMIENTO],
  ]);
  if (missingFields.length === 0) {
    return null;
  }
  return {
    rowNumber,
    type: "missingFields",
    nombreRequerimiento: rowData.nombreRequerimiento || "",
    missingFields,
  };
};

const requirementUserIssue = (rowData, rowNumber, participantUsers, t) => {
  if (!participantUsers || !Array.isArray(participantUsers)) {
    return {
      issue: {
        rowNumber,
        type: "invalidUser",
        nombreRequerimiento: rowData.nombreRequerimiento,
        field: EXCEL_HEADER_USUARIO_COMPRADOR,
        value: rowData.usuarioComprador,
        error: t("pages.buyerCompanyForm.excelNoParticipantUsersError"),
      },
    };
  }
  const matchedUser = findMatchingCustomerCompanyUser(
    rowData.usuarioComprador,
    participantUsers,
  );
  if (!matchedUser) {
    return {
      issue: {
        rowNumber,
        type: "invalidUser",
        nombreRequerimiento: rowData.nombreRequerimiento,
        field: EXCEL_HEADER_USUARIO_COMPRADOR,
        value: rowData.usuarioComprador,
        error: t("pages.buyerCompanyForm.excelInvalidParticipantUserError", {
          user: rowData.usuarioComprador,
        }),
      },
    };
  }
  return { matchedUser };
};

const requirementCategoryIssue = (rowData, rowNumber, categories, t) => {
  const matchedCategory = findMatchingCategory(rowData.categoria, categories);
  if (!matchedCategory) {
    return {
      issue: {
        rowNumber,
        type: "invalidCategory",
        nombreRequerimiento: rowData.nombreRequerimiento,
        field: EXCEL_HEADER_CATEGORIA,
        value: rowData.categoria,
        error: t("pages.buyerCompanyForm.excelInvalidCategoryError", {
          category: rowData.categoria,
        }),
      },
    };
  }
  const matchedSubcategory = findMatchingSubcategory(
    rowData.subcategoria,
    matchedCategory,
  );
  if (!matchedSubcategory) {
    return {
      issue: {
        rowNumber,
        type: "invalidSubcategory",
        nombreRequerimiento: rowData.nombreRequerimiento,
        field: EXCEL_HEADER_SUBCATEGORIA,
        value: rowData.subcategoria,
        error: t("pages.buyerCompanyForm.excelInvalidSubcategoryError", {
          subcategory: rowData.subcategoria,
          category: matchedCategory.nombre,
        }),
      },
    };
  }
  return { matchedCategory, matchedSubcategory };
};

const requirementStatusValue = (matchedStatus) => {
  switch (matchedStatus) {
    case STATUS_OPTIONAL:
      return STATUS_VALUE_OPTIONAL;
    case STATUS_NOT_REQUIRED:
      return STATUS_VALUE_NOT_REQUIRED;
    case STATUS_REQUIRED:
      return STATUS_VALUE_REQUIRED;
    default:
      return STATUS_VALUE_OPTIONAL;
  }
};

const missingRequirementFieldNames = (row, headers, statusFields) => {
  const missingRequirementFields = [];
  for (const fieldName of statusFields) {
    if (!readExcelCell(row, headers, fieldName)) {
      missingRequirementFields.push(fieldName);
    }
  }
  return missingRequirementFields;
};

const buildRequirementRequisitos = (row, headers, statusFields, fieldMapping) => {
  const invalidStatusFields = [];
  const requisitos = [];
  for (const fieldName of statusFields) {
    const fieldValue = readExcelCell(row, headers, fieldName);
    const matchedStatus = findMatchingRequirementStatus(fieldValue);
    if (!matchedStatus) {
      invalidStatusFields.push({ field: fieldName, value: fieldValue });
      continue;
    }
    const mapping = fieldMapping[fieldName];
    if (!mapping) {
      continue;
    }
    const requisito = {
      key: mapping.key,
      value: requirementStatusValue(matchedStatus),
    };
    if (mapping.name) {
      requisito.name = mapping.name;
    }
    if (mapping.certificationOptionIndex !== undefined) {
      requisito.certificationOptionIndex = mapping.certificationOptionIndex;
    }
    requisitos.push(requisito);
  }
  return { invalidStatusFields, requisitos };
};

const processRequirementRow = (ctx) => {
  const rowData = readRequirementCells(ctx.row, ctx.headers);
  if (isBlankExcelRow(rowData.mainFields)) {
    return;
  }
  const missingFieldsIssue = requirementMissingFieldsIssue(
    rowData,
    ctx.rowNumber,
  );
  if (missingFieldsIssue) {
    ctx.invalidRows.push(missingFieldsIssue);
    return;
  }
  const userResult = requirementUserIssue(
    rowData,
    ctx.rowNumber,
    ctx.validationFunctions.participantUsers,
    ctx.t,
  );
  if (userResult.issue) {
    ctx.invalidRows.push(userResult.issue);
    return;
  }
  const categoryResult = requirementCategoryIssue(
    rowData,
    ctx.rowNumber,
    ctx.validationFunctions.categories,
    ctx.t,
  );
  if (categoryResult.issue) {
    ctx.invalidRows.push(categoryResult.issue);
    return;
  }
  const missingRequirementFields = missingRequirementFieldNames(
    ctx.row,
    ctx.headers,
    ctx.statusFields,
  );
  if (missingRequirementFields.length > 0) {
    ctx.invalidRows.push({
      rowNumber: ctx.rowNumber,
      type: "missingRequirementFields",
      nombreRequerimiento: rowData.nombreRequerimiento,
      error: ctx.t("pages.buyerCompanyForm.excelMissingRequirementFieldsError"),
    });
    return;
  }
  const { invalidStatusFields, requisitos } = buildRequirementRequisitos(
    ctx.row,
    ctx.headers,
    ctx.statusFields,
    ctx.fieldMapping,
  );
  if (invalidStatusFields.length > 0) {
    ctx.invalidRows.push({
      rowNumber: ctx.rowNumber,
      type: "invalidStatusFields",
      nombreRequerimiento: rowData.nombreRequerimiento,
      invalidStatusFields,
      error: ctx.t("pages.buyerCompanyForm.excelInvalidStatusFieldsError", {
        fields: invalidStatusFields.map((field) => field.field).join(", "),
      }),
    });
    return;
  }
  ctx.validRequirements.push({
    nombre: rowData.nombreRequerimiento,
    idUsuarioComprador: userResult.matchedUser._id,
    idCategoria: categoryResult.matchedCategory._id,
    nombreSubcategoria: categoryResult.matchedSubcategory.nombre,
    detalleTecnico: rowData.descripcion,
    ejemploTecnico: null,
    nuevaImagenCargada: false,
    requisitos,
  });
};

export const validateBuyerCompaniesExcelData = async (
  data,
  validationFunctions,
  t,
) => {
  const tooShort = excelTooShortResult(data, t);
  if (tooShort) {
    return tooShort;
  }
  const headers = data[0];
  const {
    companySizeOptions,
    countryCodeOptions,
    existingCompanies = [],
    getCustomerCompanyUsersByCompanyId,
    navigate,
  } = validationFunctions;
  const duplicateHeaders = duplicateHeaderResult(headers, t);
  if (duplicateHeaders) {
    return duplicateHeaders;
  }
  const missingCompanyHeaders = missingHeadersResult(
    headers,
    BUYER_COMPANY_REQUIRED_HEADERS,
    t,
    "pages.buyerCompanyForm.excelMissingCompanyHeadersError",
  );
  if (missingCompanyHeaders) {
    return missingCompanyHeaders;
  }
  const missingUserHeaders = missingHeadersResult(
    headers,
    BUYER_USER_REQUIRED_HEADERS,
    t,
    "pages.buyerCompanyForm.excelMissingUserHeadersError",
  );
  if (missingUserHeaders) {
    return missingUserHeaders;
  }

  const invalidRows = [];
  const companyGroups = new Map();
  const existingUsersByCompanyId = new Map();
  for (let i = 1; i < data.length; i++) {
    await processBuyerCompanyRow({
      row: data[i],
      rowNumber: i + 1,
      headers,
      validationFunctions,
      companySizeOptions,
      countryCodeOptions,
      existingCompanies,
      getCustomerCompanyUsersByCompanyId,
      navigate,
      t,
      invalidRows,
      companyGroups,
      existingUsersByCompanyId,
    });
  }
  return excelRowErrorResult(invalidRows, t, {
    validCompanies: Array.from(companyGroups.values()),
  });
};

export const validateRequirementsExcelData = (data, validationFunctions, t) => {
  const tooShort = excelTooShortResult(data, t);
  if (tooShort) {
    return tooShort;
  }
  const headers = data[0];
  const requirementFields = getRequirementsExcelFieldDefinitions(t);
  const requirementHeaders = requirementFields.map((field) => field.header);
  const requiredHeaders = [...REQUIREMENT_FIXED_HEADERS, ...requirementHeaders];
  const duplicateHeaders = duplicateHeaderResult(headers, t);
  if (duplicateHeaders) {
    return duplicateHeaders;
  }
  const missingHeaders = missingHeadersResult(
    headers,
    requiredHeaders,
    t,
    "pages.buyerCompanyForm.excelMissingHeadersError",
  );
  if (missingHeaders) {
    return missingHeaders;
  }

  const fieldMapping = {};
  requirementFields.forEach((field) => {
    fieldMapping[field.header] = field;
  });
  const invalidRows = [];
  const validRequirements = [];
  for (let i = 1; i < data.length; i++) {
    processRequirementRow({
      row: data[i],
      rowNumber: i + 1,
      headers,
      validationFunctions,
      t,
      statusFields: requirementHeaders,
      fieldMapping,
      invalidRows,
      validRequirements,
    });
  }
  return excelRowErrorResult(invalidRows, t, { validRequirements });
};
